/**
 * Coaching Types
 *
 * Story 2.4: DeepGram + Local Gate + Gemini Coaching Engine
 *
 * Defines types for the three-layer coaching architecture:
 * 1. Always-On Layer: DeepGram Transcription
 * 2. Decision Layer: Local Gate (runs in Electron main process)
 * 3. Coaching Layer: Gemini suggestion generation
 *
 * Plus Chat Layer with RAG-first, web_search fallback.
 */
import { ClientTone } from './meeting-analysis.js';
import { CoachingMomentCategory } from './coaching-ws.js';
// =============================================================================
// LLM Gate Types
// =============================================================================
/** Gate trigger reasons for coaching decisions */
export const GateTrigger = {
    COMPETITOR_COMPARISON: 'competitor_comparison',
    PRICING: 'pricing',
    NEXT_STEPS: 'next_steps',
    RISK: 'risk',
    SENTIMENT_DROP: 'sentiment_drop',
    OTHER: 'other',
};
/** Gate urgency levels */
export const GateUrgency = {
    LOW: 'low',
    MEDIUM: 'medium',
    HIGH: 'high',
};
/** Gate response mode */
export const GateMode = {
    AUDIO_COACH: 'audio_coach',
    TEXT_HINT: 'text_hint',
    DO_NOTHING: 'do_nothing',
};
// =============================================================================
// Coaching Output Types
// =============================================================================
/** Category for flashcard suggestions */
export const FlashcardCategory = {
    TALK_ABOUT: 'talk-about',
    ASK_ABOUT: 'ask-about',
    ANSWER: 'answer', // AI is directly answering a client's sales question
    NEGOTIATE: 'negotiate', // AI is making a negotiation move on price/terms (probe driver, justify value, hold/concede)
    CLOSE: 'close', // AI suggests a professional close summarizing actions and next steps
    SKIP: 'skip', // AI has nothing useful to add (small talk / not sales-related)
};
/** Risk signal types detected during coaching */
export const RiskSignalType = {
    BUDGET_UNCERTAINTY: 'budget_uncertainty',
    TIMELINE_STALL: 'timeline_stall',
    COMPETITOR_PREFERENCE: 'competitor_preference',
    AUTHORITY_UNCLEAR: 'authority_unclear',
};
/**
 * JSON Schema for the coaching suggestion model output. Used as
 * `response_format: { type: 'json_schema', json_schema: { strict: true, schema: ... } }`
 * on the Cerebras chat-completions request so the model returns
 * guaranteed-valid JSON matching the Flashcard / RiskSignal contract.
 */
export const COACHING_SUGGESTION_SCHEMA = {
    type: 'object',
    additionalProperties: false,
    required: [
        'category',
        'content',
        'clientTone',
        'riskSignals',
        'momentCategory',
        'coachingGoalId',
        'coachingGoalCategory',
    ],
    properties: {
        category: {
            enum: Object.values(FlashcardCategory),
        },
        content: { type: 'string' },
        /**
         * Classification of the current conversational moment. Drives per-rep
         * goal-chip routing downstream (see `chip-resolver-service.ts`). The model
         * picks exactly one - mirrors `CoachingMomentCategory` in `coaching-ws.ts`.
         */
        momentCategory: {
            enum: Object.values(CoachingMomentCategory),
        },
        clientTone: {
            anyOf: [
                {
                    enum: Object.values(ClientTone),
                },
                { type: 'null' },
            ],
        },
        riskSignals: {
            type: 'array',
            items: {
                type: 'object',
                additionalProperties: false,
                required: ['type', 'evidence'],
                properties: {
                    type: {
                        enum: Object.values(RiskSignalType),
                    },
                    evidence: { type: 'string' },
                },
            },
        },
        /**
         * Path B (goal-driven steering): when the engine decides to surface an
         * active coaching goal's title as the suggestion content, this is the
         * matched goal's id (copied verbatim from input). Null in every other
         * case - KB-derived, memory-derived, or no-goal-alignment fires.
         */
        coachingGoalId: {
            anyOf: [{ type: 'string' }, { type: 'null' }],
        },
        /**
         * Companion to `coachingGoalId`. The category of the matched active goal
         * (copied verbatim from input). Null when `coachingGoalId` is null.
         */
        coachingGoalCategory: {
            anyOf: [
                {
                    enum: ['opening', 'discovery', 'objection_handling', 'close', 'overall'],
                },
                { type: 'null' },
            ],
        },
    },
};
/** Sentiment labels for coaching analysis */
export const SentimentLabel = {
    CONFIDENT: 'confident',
    HESITANT: 'hesitant',
    INTERESTED: 'interested',
    SKEPTICAL: 'skeptical',
    NEUTRAL: 'neutral',
};
/** Default coaching configuration values */
export const DEFAULT_COACHING_CONFIG = {
    cooldownSeconds: 15,
    gateTimeoutMs: 2000,
    transcriptBufferSeconds: 20,
    audioItemCount: 4,
    ragSimilarityThreshold: 0.5,
    minRagResults: 3,
};
// ── Coaching Page Types ──────────────────────────────────────────────────────
export const CoachingCategory = {
    OPENING: 'opening',
    DISCOVERY: 'discovery',
    OBJECTION_HANDLING: 'objection_handling',
    CLOSE: 'close',
};
